import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Table,
  Input,
  Button,
  Space,
  Typography,
  message,
  Modal,
  Form,
  Row,
  Col,
  Breadcrumb,
  Dropdown,
  Spin,
  Empty,
  Popconfirm,
  Upload,
  Select
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  DeleteOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  GlobalOutlined,
  HomeOutlined,
  FolderOutlined,
  DownloadOutlined,
  UploadOutlined,
  FileTextOutlined,
  TableOutlined,
  ClearOutlined
} from '@ant-design/icons';
import { translationProjectsService } from '../../api/translationProjects';
import { translationsService } from '../../api/translations';
import { useAuth } from '../../hooks/useAuth';
import { getAccessToken } from '../../api/axios';
// Временно используем setTimeout instead of useDebounce
// import { useDebounce } from '../../hooks/useDebounce';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface ProjectTranslation {
  key: string;
  translations: Record<string, string>; // languageId -> value
}

interface EditableTranslation {
  key: string;
  languageId: string;
  value: string;
}

const ProjectTranslations: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasRole } = useAuth();
  
  const [searchText, setSearchText] = useState('');
  const [visibleLanguages, setVisibleLanguages] = useState<string[]>([]);
  const [editingCell, setEditingCell] = useState<{ key: string; languageId: string } | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [selectedLanguageForImport, setSelectedLanguageForImport] = useState<string>('');
  const [isClearModalVisible, setIsClearModalVisible] = useState(false);
  const [selectedLanguageForClear, setSelectedLanguageForClear] = useState<string>('');
  const [filter, setFilter] = useState<'all' | 'translated' | 'untranslated'>('all');
  const [selectedLanguageFilter, setSelectedLanguageFilter] = useState<string>('');
  const [createForm] = Form.useForm();
  const [importForm] = Form.useForm();

  // Временно используем searchText напрямую без debounce
  const debouncedSearchText = searchText;

  // Fetch project details
  const { data: project, isLoading: projectLoading } = useQuery({
    queryKey: ['translationProject', projectId],
    queryFn: () => translationProjectsService.getProject(projectId!),
    enabled: !!projectId,
  });

  // Fetch active languages
  const { data: languagesData } = useQuery({
    queryKey: ['languages'],
    queryFn: () => translationsService.getLanguages({ limit: 50 }),
  });

  const activeLanguages = useMemo(() => {
    return languagesData?.items?.filter(lang => lang.isActive) || [];
  }, [languagesData]);

  // Initialize visible languages when activeLanguages change
  useEffect(() => {
    if (activeLanguages.length > 0 && visibleLanguages.length === 0) {
      setVisibleLanguages(activeLanguages.map(lang => lang.id));
    }
  }, [activeLanguages, visibleLanguages.length]);

  // Fetch project translations with filters
  const { data: projectTranslationsData, isLoading: translationsLoading } = useQuery({
    queryKey: ['projectTranslations', projectId, filter, selectedLanguageFilter],
    queryFn: async () => {
      let url = `https://oxcorp.oxapp.io/api/v1/translations/projects/${projectId}/translations`;
      const params = new URLSearchParams();
      
      if (filter !== 'all') {
        params.append('filter', filter);
      }
      
      if (selectedLanguageFilter) {
        params.append('language', selectedLanguageFilter);
      }
      
      if (params.toString()) {
        url += '?' + params.toString();
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${getAccessToken()}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return response.json();
    },
    enabled: !!projectId,
  });

  // Process translations data into table format
  const translationKeysData = useMemo(() => {
    if (!projectTranslationsData || !projectTranslationsData.translations) return [];

    const keysMap = new Map<string, Record<string, string>>();
    
    console.log('🔍 Processing translations data:', projectTranslationsData);
    console.log('🔍 Active languages:', activeLanguages);
    console.log('🔍 Current access token:', getAccessToken());
    
    // Create a mapping from language codes to language IDs
    const codeToIdMap = new Map<string, string>();
    activeLanguages.forEach(lang => {
      codeToIdMap.set(lang.code, lang.id);
    });
    
    console.log('🔍 Code to ID mapping:', Array.from(codeToIdMap.entries()));
    
    // Process translations for each language
    // API returns: { translations: { "en": { "key1": "value1", "key2": "value2" }, "ru": { ... } } }
    Object.entries(projectTranslationsData.translations).forEach(([languageCode, translations]) => {
      console.log(`🔍 Processing language ${languageCode}:`, Object.keys(translations || {}).length, 'keys');
      
      if (translations && typeof translations === 'object') {
        // Use language ID from activeLanguages, fallback to code if not found
        const languageId = codeToIdMap.get(languageCode) || languageCode;
        
        Object.entries(translations).forEach(([key, value]) => {
          if (!key) return;
          
          if (!keysMap.has(key)) {
            keysMap.set(key, {});
          }
          keysMap.get(key)![languageId] = value || '';
        });
      }
    });

    const result = Array.from(keysMap.entries()).map(([key, translations]) => ({
      key,
      translations,
    }));
    
    console.log('🔍 Final processed data:', result.slice(0, 3)); // First 3 items for debugging
    
    return result;
  }, [projectTranslationsData, activeLanguages]);

  // Filter translations based on search
  const filteredTranslations = useMemo(() => {
    if (!debouncedSearchText) return translationKeysData;
    
    const search = debouncedSearchText.toLowerCase();
    return translationKeysData.filter(item => {
      if (!item || !item.key || !item.translations) return false;
      
      const keyMatch = item.key.toLowerCase().includes(search);
      const valueMatch = Object.values(item.translations).some(value => 
        value?.toLowerCase().includes(search)
      );
      return keyMatch || valueMatch;
    });
  }, [translationKeysData, debouncedSearchText]);

  // Create translation mutation
  const createTranslationMutation = useMutation({
    mutationFn: async (data: { key: string; translations: Record<string, string> }) => {
      const handleAddTranslation = async (data: { key: string; language: string; value: string }) => {
        try {
          await translationProjectsService.createProjectTranslation(
            projectId!,
            data.language,
            data.key,
            data.value
          );
        } catch (error) {
          console.error('Error adding translation:', error);
          throw error;
        }
      };

      const promises = Object.entries(data.translations).map(([languageId, value]) => {
        if (value.trim()) {
          return handleAddTranslation({ key: data.key, language: languageId, value: value.trim() });
        }
        return Promise.resolve();
      });
      
      await Promise.all(promises.filter(Boolean));
    },
    onSuccess: () => {
      message.success('Переводы созданы успешно');
      setIsCreateModalVisible(false);
      createForm.resetFields();
      queryClient.invalidateQueries({ queryKey: ['projectTranslations', projectId] });
    },
    onError: (error: Error) => {
      message.error(`Ошибка при создании переводов: ${error.message}`);
    },
  });

  // Update translation mutation
  const updateTranslationMutation = useMutation({
    mutationFn: async ({ key, languageId, value }: EditableTranslation) => {
      return await translationProjectsService.updateProjectTranslation(
        projectId!,
        languageId,
        key,
        value
      );
    },
    onSuccess: () => {
      message.success('Перевод обновлен');
      queryClient.invalidateQueries({ queryKey: ['projectTranslations', projectId] });
    },
    onError: (error: Error) => {
      message.error(`Ошибка при обновлении перевода: ${error.message}`);
    },
  });

  // Delete translation mutation
  const deleteTranslationMutation = useMutation({
    mutationFn: async ({ key, languageId }: { key: string; languageId: string }) => {
      return await translationProjectsService.deleteProjectTranslation(
        projectId!,
        languageId,
        key
      );
    },
    onSuccess: () => {
      message.success('Перевод удален');
      queryClient.invalidateQueries({ queryKey: ['projectTranslations', projectId] });
    },
    onError: (error: Error) => {
      message.error(`Ошибка при удалении перевода: ${error.message}`);
    },
  });

  // Clear language translations mutation
  const clearLanguageTranslationsMutation = useMutation({
    mutationFn: async (languageId: string) => {
      return await translationsService.clearProjectLanguageTranslations(projectId!, languageId);
    },
    onSuccess: () => {
      message.success('Переводы очищены');
      queryClient.invalidateQueries({ queryKey: ['projectTranslations', projectId] });
      setIsClearModalVisible(false);
      setSelectedLanguageForClear('');
    },
    onError: (error: Error) => {
      message.error(`Ошибка при очистке переводов: ${error.message}`);
    },
  });

  // Handle cell edit
  const handleCellEdit = (key: string, languageId: string, currentValue: string) => {
    setEditingCell({ key, languageId });
    setEditValues({ [`${key}-${languageId}`]: currentValue || '' });
  };

  // Handle save edit
  const handleSaveEdit = () => {
    if (!editingCell) return;

    const editKey = `${editingCell.key}-${editingCell.languageId}`;
    const newValue = editValues[editKey];

    updateTranslationMutation.mutate({
      key: editingCell.key,
      languageId: editingCell.languageId,
      value: newValue,
    });

    setEditingCell(null);
    setEditValues({});
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingCell(null);
    setEditValues({});
  };

  // Handle create translation
  const handleCreateTranslation = (values: Record<string, string>) => {
    const translations: Record<string, string> = {};
    
    activeLanguages.forEach(lang => {
      const value = values[lang.id];
      if (value && value.trim()) {
        translations[lang.id] = value.trim();
      }
    });

    createTranslationMutation.mutate({
      key: values.key,
      translations,
    });
  };

  // Handle clear translations
  const handleClearTranslations = () => {
    if (!selectedLanguageForClear) {
      message.warning('Выберите язык для очистки переводов');
      return;
    }
    
    clearLanguageTranslationsMutation.mutate(selectedLanguageForClear);
  };

  const handleClearModalCancel = () => {
    setIsClearModalVisible(false);
    setSelectedLanguageForClear('');
  };

  // Export translations
  const handleExport = async (format: 'json' | 'csv', languageId: string) => {
    try {
      const language = activeLanguages.find(lang => lang.id === languageId);
      if (!language) return;

      // Get translations for this language from our data
      const languageTranslations: Record<string, string> = {};
      translationKeysData.forEach(item => {
        if (item.translations[languageId]) {
          languageTranslations[item.key] = item.translations[languageId];
        }
      });

      let content: string;
      let filename: string;
      let mimeType: string;

      if (format === 'json') {
        content = JSON.stringify(languageTranslations, null, 2);
        filename = `${project?.name}_${language.code}_translations.json`;
        mimeType = 'application/json';
      } else {
        // CSV format
        const csvRows = ['Key,Value'];
        Object.entries(languageTranslations).forEach(([key, value]) => {
          // Escape quotes and wrap in quotes if contains comma
          const escapedValue = value.includes(',') || value.includes('"') 
            ? `"${value.replace(/"/g, '""')}"` 
            : value;
          csvRows.push(`"${key}",${escapedValue}`);
        });
        content = csvRows.join('\n');
        filename = `${project?.name}_${language.code}_translations.csv`;
        mimeType = 'text/csv';
      }

      // Create and download file
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      message.success(`Переводы экспортированы в ${format.toUpperCase()}`);
    } catch (error) {
      message.error('Ошибка при экспорте переводов');
      console.error('Export error:', error);
    }
  };

  // Import translations
  const handleImport = (values: { language: string; file: { file: File } }) => {
    const file = values.file?.file;
    if (!file || !selectedLanguageForImport) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const content = e.target?.result as string;
        let translations: Record<string, string> = {};

        if (file.name.endsWith('.json')) {
          translations = JSON.parse(content);
        } else if (file.name.endsWith('.csv')) {
          const lines = content.split('\n');
          for (let i = 1; i < lines.length; i++) { // Skip header
            const line = lines[i].trim();
            if (line) {
              const match = line.match(/^"([^"]*)",(.*)$/);
              if (match) {
                const key = match[1];
                const value = match[2].replace(/^"|"$/g, ''); // Remove quotes
                translations[key] = value;
              }
            }
          }
        }
        
        // Make API call to import translations
        const result = await translationProjectsService.importProjectTranslations(
          projectId!,
          selectedLanguageForImport,
          translations
        );
        
        message.success(`Импортировано ${result.imported || Object.keys(translations).length} переводов`);
        setIsImportModalVisible(false);
        importForm.resetFields();
        setSelectedLanguageForImport('');
        queryClient.invalidateQueries({ queryKey: ['projectTranslations', projectId] });
      } catch (error) {
        message.error('Ошибка при импорте переводов');
        console.error('Import error:', error);
      }
    };

    reader.readAsText(file);
  };

  // Define table columns
  const columns = [
    {
      title: 'Ключ',
      dataIndex: 'key',
      key: 'key',
      width: 250,
      fixed: 'left' as const,
      render: (key: string) => (
        <Text code copyable={{ text: key }}>
          {key}
        </Text>
      ),
    },
    ...visibleLanguages.map(languageId => {
      const language = activeLanguages.find(lang => lang.id === languageId);
      if (!language) return undefined;

      return {
        title: (
          <Space>
            <span>{language.name}</span>
            <Text type="secondary">({language.code})</Text>
          </Space>
        ),
        key: languageId,
        width: 250,
        render: (record: ProjectTranslation) => {
          if (!record || !record.key || !record.translations || typeof record.translations !== 'object') {
            return <Text type="secondary">—</Text>;
          }
          
          const value = record.translations[languageId] || '';
          const isEditing = editingCell?.key === record.key && editingCell?.languageId === languageId;
          const editKey = `${record.key}-${languageId}`;

          if (isEditing) {
            return (
              <Space.Compact style={{ width: '100%' }}>
                <TextArea
                  value={editValues[editKey]}
                  onChange={(e) => setEditValues(prev => ({
                    ...prev,
                    [editKey]: e.target.value
                  }))}
                  autoSize={{ minRows: 1, maxRows: 3 }}
                  style={{ flex: 1 }}
                />
                <Button type="primary" size="small" onClick={handleSaveEdit}>
                  ✓
                </Button>
                <Button size="small" onClick={handleCancelEdit}>
                  ✕
                </Button>
              </Space.Compact>
            );
          }

          return (
            <div
              style={{ 
                minHeight: '32px', 
                cursor: hasRole(['ADMIN', 'SUPERADMIN']) ? 'pointer' : 'default',
                padding: '4px 8px',
                borderRadius: '4px',
                backgroundColor: !value ? '#fff2f0' : 'transparent',
                border: !value ? '1px dashed #ffccc7' : '1px solid transparent',
              }}
              onClick={() => {
                if (hasRole(['ADMIN', 'SUPERADMIN'])) {
                  handleCellEdit(record.key, languageId, value);
                }
              }}
              onMouseEnter={(e) => {
                if (hasRole(['ADMIN', 'SUPERADMIN'])) {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                }
              }}
              onMouseLeave={(e) => {
                if (hasRole(['ADMIN', 'SUPERADMIN'])) {
                  e.currentTarget.style.backgroundColor = !value ? '#fff2f0' : 'transparent';
                }
              }}
            >
              {value || <Text type="secondary" italic>Пусто</Text>}
              {hasRole(['ADMIN', 'SUPERADMIN']) && value && (
                <Popconfirm
                  title="Удалить этот перевод?"
                  onConfirm={(e) => {
                    e?.stopPropagation();
                    deleteTranslationMutation.mutate({ key: record.key, languageId });
                  }}
                >
                  <Button
                    type="text"
                    size="small"
                    icon={<DeleteOutlined />}
                    style={{ opacity: 0.5, marginLeft: 8 }}
                    danger
                    onClick={(e) => e?.stopPropagation()}
                  />
                </Popconfirm>
              )}
            </div>
          );
        },
      };
    }).filter(col => col !== undefined),
  ];

  // Language visibility menu
  const languageVisibilityMenu = {
    items: activeLanguages.map(lang => ({
      key: lang.id,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minWidth: 200 }}>
          <Space>
            <span>{lang.name}</span>
            <Text type="secondary">({lang.code})</Text>
          </Space>
          {visibleLanguages.includes(lang.id) ? <EyeOutlined /> : <EyeInvisibleOutlined />}
        </div>
      ),
      onClick: () => {
        setVisibleLanguages(prev => 
          prev.includes(lang.id) 
            ? prev.filter(id => id !== lang.id)
            : [...prev, lang.id]
        );
      },
    })),
  };

  if (projectLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!project) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Empty description="Проект не найден" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      {/* Breadcrumb */}
      <Breadcrumb style={{ marginBottom: 24 }}>
        <Breadcrumb.Item onClick={() => navigate('/')}>
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item onClick={() => navigate('/translations/projects')}>
          <FolderOutlined />
          <span>Проекты</span>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <GlobalOutlined />
          <span>{project.displayName}</span>
        </Breadcrumb.Item>
      </Breadcrumb>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <GlobalOutlined style={{ marginRight: 8 }} />
          Переводы проекта: {project.displayName}
        </Title>
        <Text type="secondary">
          Системное название: <Text code>{project.name}</Text>
          {project.description && ` • ${project.description}`}
        </Text>
      </div>

      {/* Statistics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary">Всего ключей</Text>
              <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {translationKeysData.length}
              </div>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary">Активных языков</Text>
              <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {activeLanguages.length}
              </div>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary">Видимых языков</Text>
              <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {visibleLanguages.length}
              </div>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary">
                {filter === 'translated' ? 'Переведенных' : 
                 filter === 'untranslated' ? 'Непереведенных' : 
                 'Найдено'}
                {selectedLanguageFilter && (
                  <div style={{ fontSize: '10px', marginTop: 2 }}>
                    ({activeLanguages.find(l => l.id === selectedLanguageFilter)?.code || ''})
                  </div>
                )}
              </Text>
              <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {filteredTranslations.length}
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <Card>
        <Space direction="vertical" style={{ width: '100%' }}>
          {/* Active filters indicator */}
          {(filter !== 'all' || selectedLanguageFilter || searchText) && (
            <div style={{ padding: '8px 12px', background: '#f0f2f5', borderRadius: '6px' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Активные фильтры: {' '}
                {filter !== 'all' && (
                  <span style={{ color: '#1890ff' }}>
                    Статус: {filter === 'translated' ? 'Переведенные' : 'Непереведенные'}
                    {' '}
                  </span>
                )}
                {selectedLanguageFilter && (
                  <span style={{ color: '#52c41a' }}>
                    Язык: {activeLanguages.find(l => l.id === selectedLanguageFilter)?.name}
                    {' '}
                  </span>
                )}
                {searchText && (
                  <span style={{ color: '#fa8c16' }}>
                    Поиск: &ldquo;{searchText}&rdquo;
                  </span>
                )}
              </Text>
            </div>
          )}
          
          {/* Filters and controls */}
          <Row justify="space-between" align="middle" gutter={16}>
            <Col span={8}>
              <Input
                placeholder="Поиск по ключу или значению..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: '100%' }}
              />
            </Col>
            <Col span={4}>
              <Select
                placeholder="Статус перевода"
                value={filter}
                onChange={setFilter}
                style={{ width: '100%' }}
              >
                <Select.Option value="all">Все переводы</Select.Option>
                <Select.Option value="translated">Переведенные</Select.Option>
                <Select.Option value="untranslated">Непереведенные</Select.Option>
              </Select>
            </Col>
            <Col span={4}>
              <Select
                placeholder="Фильтр по языку"
                value={selectedLanguageFilter}
                onChange={setSelectedLanguageFilter}
                style={{ width: '100%' }}
                allowClear
              >
                {activeLanguages.map(lang => (
                  <Select.Option key={lang.id} value={lang.id}>
                    {lang.name} ({lang.code})
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col>
              <Space>
                <Dropdown menu={languageVisibilityMenu} trigger={['click']}>
                  <Button icon={<EyeOutlined />}>
                    Языки ({visibleLanguages.length})
                  </Button>
                </Dropdown>
                
                {/* Export Dropdown */}
                <Dropdown
                  menu={{
                    items: activeLanguages.map(lang => ({
                      key: lang.id,
                      label: (
                        <div>
                          <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                            {lang.name} ({lang.code})
                          </div>
                          <Space>
                            <Button
                              type="text"
                              size="small"
                              icon={<FileTextOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleExport('json', lang.id);
                              }}
                            >
                              JSON
                            </Button>
                            <Button
                              type="text"
                              size="small"
                              icon={<TableOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleExport('csv', lang.id);
                              }}
                            >
                              CSV
                            </Button>
                          </Space>
                        </div>
                      ),
                    })),
                  }}
                  trigger={['click']}
                >
                  <Button icon={<DownloadOutlined />}>
                    Экспорт
                  </Button>
                </Dropdown>

                {/* Reset filters button */}
                {(filter !== 'all' || selectedLanguageFilter || searchText) && (
                  <Button
                    icon={<ClearOutlined />}
                    onClick={() => {
                      setFilter('all');
                      setSelectedLanguageFilter('');
                      setSearchText('');
                    }}
                  >
                    Сбросить фильтры
                  </Button>
                )}
                
                {hasRole(['ADMIN', 'SUPERADMIN']) && (
                  <>
                    <Button
                      icon={<UploadOutlined />}
                      onClick={() => setIsImportModalVisible(true)}
                    >
                      Импорт
                    </Button>
                    <Button
                      danger
                      icon={<ClearOutlined />}
                      onClick={() => setIsClearModalVisible(true)}
                    >
                      Очистить переводы
                    </Button>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => setIsCreateModalVisible(true)}
                    >
                      Добавить перевод
                    </Button>
                  </>
                )}
              </Space>
            </Col>
          </Row>

          {/* Main table */}
          {translationsLoading ? (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <Spin size="large" />
            </div>
          ) : filteredTranslations.length === 0 ? (
            <Empty description="Нет данных" />
          ) : (
            <Table
              columns={columns as import('antd').TableProps<ProjectTranslation>['columns']}
              dataSource={filteredTranslations.filter(item => item && item.key && item.translations)}
              rowKey="key"
              loading={updateTranslationMutation.isPending || deleteTranslationMutation.isPending}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showQuickJumper: true,
              }}
              scroll={{ x: Math.max(500, 250 * (visibleLanguages.length + 1)) }}
            />
          )}
        </Space>
      </Card>

      {/* Create Translation Modal */}
      <Modal
        title="Добавить новый перевод"
        open={isCreateModalVisible}
        onCancel={() => {
          setIsCreateModalVisible(false);
          createForm.resetFields();
        }}
        onOk={() => createForm.submit()}
        confirmLoading={createTranslationMutation.isPending}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateTranslation}
        >
          <Form.Item
            label="Ключ перевода"
            name="key"
            rules={[
              { required: true, message: 'Пожалуйста, введите ключ перевода!' },
              { pattern: /^[a-zA-Z0-9._-]+$/, message: 'Ключ может содержать только латинские буквы, цифры, точки, дефисы и подчеркивания' },
            ]}
          >
            <Input placeholder="example.translation.key" />
          </Form.Item>

          {activeLanguages.map(lang => (
            <Form.Item
              key={lang.id}
              label={
                <Space>
                  <span>{lang.name}</span>
                  <Text type="secondary">({lang.code})</Text>
                </Space>
              }
              name={lang.id}
            >
              <TextArea
                placeholder={`Введите перевод на ${lang.nativeName}...`}
                autoSize={{ minRows: 2, maxRows: 4 }}
              />
            </Form.Item>
          ))}
        </Form>
      </Modal>

      {/* Import Modal */}
      <Modal
        title="Импорт переводов"
        open={isImportModalVisible}
        onCancel={() => {
          setIsImportModalVisible(false);
          importForm.resetFields();
          setSelectedLanguageForImport('');
        }}
        onOk={() => importForm.submit()}
        okText="Импортировать"
        cancelText="Отмена"
      >
        <Form
          form={importForm}
          layout="vertical"
          onFinish={handleImport}
        >
          <Form.Item
            label="Язык для импорта"
            name="language"
            rules={[{ required: true, message: 'Выберите язык для импорта!' }]}
          >
            <Select
              placeholder="Выберите язык..."
              value={selectedLanguageForImport}
              onChange={setSelectedLanguageForImport}
            >
              {activeLanguages.map(lang => (
                <Select.Option key={lang.id} value={lang.id}>
                  {lang.name} ({lang.code})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Файл переводов"
            name="file"
            rules={[{ required: true, message: 'Загрузите файл!' }]}
          >
            <Upload.Dragger
              name="file"
              multiple={false}
              beforeUpload={() => false}
              accept=".json,.csv"
              maxCount={1}
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">Нажмите или перетащите файл сюда</p>
              <p className="ant-upload-hint">
                Поддерживаются форматы JSON и CSV
              </p>
            </Upload.Dragger>
          </Form.Item>

          <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 6, marginTop: 16 }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <div><strong>Формат JSON:</strong> {'{"key1": "value1", "key2": "value2"}'}</div>
              <div style={{ marginTop: 4 }}><strong>Формат CSV:</strong> Key,Value заголовки с данными в строках</div>
            </Text>
          </div>
        </Form>
      </Modal>

      {/* Clear Translations Modal */}
      <Modal
        title="Очистить переводы"
        open={isClearModalVisible}
        onCancel={handleClearModalCancel}
        onOk={handleClearTranslations}
        confirmLoading={clearLanguageTranslationsMutation.isPending}
        okText="Очистить"
        cancelText="Отмена"
        okButtonProps={{ danger: true }}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            Выберите язык для очистки всех переводов в этом проекте:
          </Text>
        </div>
        
        <Select
          placeholder="Выберите язык"
          style={{ width: '100%' }}
          value={selectedLanguageForClear}
          onChange={setSelectedLanguageForClear}
          options={activeLanguages.map(lang => ({
            label: `${lang.name} (${lang.code})`,
            value: lang.id,
          }))}
        />
        
        {selectedLanguageForClear && (
          <div style={{ 
            background: '#fff2f0', 
            border: '1px solid #ffccc7', 
            padding: 12, 
            borderRadius: 6, 
            marginTop: 16 
          }}>
            <Text type="danger" style={{ fontSize: '12px' }}>
              <strong>Внимание!</strong> Все переводы выбранного языка будут безвозвратно удалены из этого проекта.
            </Text>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ProjectTranslations;
